// NPM imports
import { useState, useCallback } from 'react';

// Local imports
import {
  getAssertionPayload,
  base64ToUint8Array,
} from '../utils/passkeyHelpers';
import devLog from '../utils/devLog';
import SHARED from '../utils/sharedConstants';

const { FLOW_TYPE } = SHARED;

// ─── Passkey Helpers ───────────────────────────────────────────────────────────

/**
 * Builds PublicKeyCredentialCreationOptions (type='create') or
 * PublicKeyCredentialRequestOptions (type='get') dynamically.
 */
const buildPublicKeyOptions = (options = {}, type = 'create') => {
  const { challenge, isPayment = false } = options;

  if (type === 'get') {
    const { credentialIds } = options;
    const allowCredentials = (credentialIds || []).map((id) => ({
      id: base64ToUint8Array(id),
      type: 'public-key',
      transports: ['internal', 'usb', 'ble', 'nfc'],
    }));

    return {
      challenge: base64ToUint8Array(challenge),
      allowCredentials,
      timeout: 60000,
      userVerification: 'preferred',
      rpId: 'americanexpress.com',
    };
  }

  // type === 'create'
  const { rp, user } = options;
  const publicKeyOptions = {
    challenge: base64ToUint8Array(challenge),
    rp,
    user: {
      ...user,
      id: base64ToUint8Array(user.id),
    },
    pubKeyCredParams: [
      { alg: -7, type: 'public-key' },
      { alg: -257, type: 'public-key' },
    ],
    authenticatorSelection: {
      authenticatorAttachment: 'platform',
      residentKey: 'required',
      requireResidentKey: true,
      userVerification: 'required',
    },
    timeout: 60000,
    attestation: 'none',
  };

  publicKeyOptions.extensions = { uvm: true };

  if (isPayment) {
    publicKeyOptions.extensions.payment = { isPayment: true };
  }

  return publicKeyOptions;
};

// ─── useCreatePasskey Hook ─────────────────────────────────────────────────────

const useCreatePasskey = (registrationOptions) => {
  const [isCreating, setIsCreating] = useState(false);
  const [credential, setCredential] = useState(null);
  const [error, setError] = useState(null);

  const create = useCallback(async (registrationOptionsOverride, isPayment = false) => {
    const activeRegistrationOptions = registrationOptionsOverride ?? registrationOptions;

    if (!activeRegistrationOptions) {
      throw new Error('registrationOptions are required to create a passkey');
    }

    setIsCreating(true);
    setError(null);

    try {
      const creationOptions = buildPublicKeyOptions(
        { ...activeRegistrationOptions, isPayment },
        'create'
      );
      const newCredential = await navigator.credentials.create({ publicKey: creationOptions });

      if (newCredential.getClientExtensionResults) {
        newCredential.extensionResults = newCredential.getClientExtensionResults();
      }

      setCredential(newCredential);
      return newCredential;
    } catch (err) {
      devLog.error('useCreatePasskey - credential creation failed:', err?.name, err?.message);
      setError(err);
      throw err;
    } finally {
      setIsCreating(false);
    }
  }, [registrationOptions]);

  const createPasskey = useCallback(
    (registrationOptionsOverride) => create(registrationOptionsOverride),
    [create]
  );

  const createSpcBbkPasskey = useCallback(
    (registrationOptionsOverride) => create(registrationOptionsOverride, true),
    [create]
  );

  return {
    isCreating,
    credential,
    error,
    createPasskey,
    createSpcBbkPasskey,
  };
};

// ─── usePasskeyAssertion Hook ──────────────────────────────────────────────────

const usePasskeyAssertion = ({ challenge, credentialIds } = {}) => {
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [error, setError] = useState(null);

  const handleAuthenticate = useCallback(async () => {
    setIsAuthenticating(true);
    setError(null);
    try {
      const publicKeyOptions = buildPublicKeyOptions(
        { challenge, credentialIds },
        'get'
      );

      const credential = await navigator.credentials.get({
        publicKey: publicKeyOptions,
      });
      if (!credential) throw new Error('No credential returned.');

      const assertionPayload = getAssertionPayload(credential);
      return {
        credentialId: assertionPayload?.id || '',
        publicKeyCredential: credential,
        authenticatorData: assertionPayload?.response?.authenticatorData || '',
        signature: assertionPayload?.response?.signature || '',
        userHandle: assertionPayload?.response?.userHandle || '',
        ceremonyType: FLOW_TYPE.AUTHENTICATION,
      };
    } catch (err) {
      devLog.error('usePasskeyAssertion - authentication failed:', err?.name, err?.message);
      setError(err);
      throw err;
    } finally {
      setIsAuthenticating(false);
    }
  }, [challenge, credentialIds]);

  return {
    isAuthenticating,
    error,
    handleAuthenticate,
  };
};

export { buildPublicKeyOptions, useCreatePasskey, usePasskeyAssertion };
export default useCreatePasskey;
